<script setup lang="ts">
import { PropType, computed, ref, watchEffect } from 'vue';
import { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';
import SelectButton from 'primevue/selectbutton';
import { PredicateAnsweredEvent } from '../helpers/types';

const props = defineProps({
    answer: {
        type: String as PropType<PredicateValue>,
        default: PredicateValue.Indeterminate,
        required: false,
    },
    predicateId: {
        type: String,
        default: undefined,
    },
    yes: {
        type: Boolean,
        default: false,
    },
    no: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: Boolean,
        default: false,
    },
    undecidable: {
        type: Boolean,
        default: false,
    },
    indeterminate: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits<{
    (e: 'answered', answer: PredicateAnsweredEvent): void;
}>();

// ------------------------------------------------------------------------------------------------------------------ //
//  OPTIONS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

interface LabeledValue {
    label: string;
    value: PredicateValue;
    icon: string;
}

const options = computed<LabeledValue[]>(() => {
    const retval: LabeledValue[] = [];

    if (props.yes)
        retval.push({
            label: 'Ja',
            icon: 'pi pi-check-circle text-green-500',
            value: PredicateValue.Yes,
        });
    if (props.no)
        retval.push({
            label: 'Nee',
            icon: 'pi pi-times-circle text-red-500',
            value: PredicateValue.No,
        });
    if (props.undecidable)
        retval.push({
            label: 'Onbekend',
            icon: 'pi-question-circle',
            value: PredicateValue.Undecidable,
        });
    if (props.indeterminate)
        retval.push({
            label: 'Niet beantwoord',
            icon: 'pi pi-exclamation-circle',
            value: PredicateValue.Indeterminate,
        });
    return retval;
});

// ------------------------------------------------------------------------------------------------------------------ //
//  STATE                                                                                                             //
// ------------------------------------------------------------------------------------------------------------------ //

const disabled = computed(
    () => props.disabled || typeof props.predicateId !== 'string'
);
const answer = computed(() => props.answer);
const selectedAnswer = ref<PredicateValue | undefined>();
watchEffect(() => {
    selectedAnswer.value = answer.value;
});

// ------------------------------------------------------------------------------------------------------------------ //
//  ACTIONS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

function onChange() {
    const predicateId = props.predicateId;
    if (!predicateId) return;

    const value = selectedAnswer.value ?? PredicateValue.Indeterminate;
    emit('answered', { predicateId, answer: value });
}
</script>

<template>
    <SelectButton
        v-model="selectedAnswer"
        :options="options"
        class="icon predicate-value-selectbutton"
        option-label="label"
        option-value="value"
        :disabled="disabled"
        @change="onChange"
    >
        <template v-if="props.icon" #option="prop">
            <i :class="prop.option.icon" />
        </template>
    </SelectButton>
</template>
<style lang="scss">
// .predicate-value-selectbutton{
// .p-button:nth-child(1){
//     background-color:var(--green-100);
// }
// .p-button:nth-child(2){
//     background-color:var(--orange-100);
// }
// .p-highlight:nth-child(1){
//     background-color:var(--green-600);
// }
// .p-highlight:nth-child(2){
//     background-color:var(--orange-600);
// }
// }
</style>
